import { API_BASE_URL } from '../../../config';
import type {
  ReferralDashboard,
  ReferralHistoryResponse,
  PlanInfo,
  UpgradeRequest,
  UpgradeResponse,
} from '../types';

const REFERRAL_API_BASE = `${API_BASE_URL}/api/v1/referrals`;
const PLAN_API_BASE = `${API_BASE_URL}/api/v1/users/me/plan`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Fetch referral dashboard data (link, totals, bonus) */
export async function fetchReferralDashboard(): Promise<ReferralDashboard> {
  const response = await fetch(`${REFERRAL_API_BASE}/me`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ReferralDashboard>(response);
}

/** Fetch referral history list */
export async function fetchReferralHistory(): Promise<ReferralHistoryResponse> {
  const response = await fetch(`${REFERRAL_API_BASE}/me/history`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ReferralHistoryResponse>(response);
}

/** Fetch current user plan info with quota usage */
export async function fetchPlanInfo(): Promise<PlanInfo> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<PlanInfo>(response);
}

/** Initiate plan upgrade via Stripe Checkout */
export async function upgradePlan(data: UpgradeRequest): Promise<UpgradeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/create-checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ plan: data.plan }),
  });
  const result = await handleResponse<{ url: string; sessionId: string }>(response);
  return { checkoutUrl: result.url, sessionId: result.sessionId };
}
